import { SlideTag, SlideTagStudyDetails } from 'interfaces/slideTag';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export type BulkSlideTagOperation = 'add' | 'remove' | 'override' | 'clear';
export type BulkSlideTagResultScopeMode = 'filter' | 'explicit' | 'approved_per_slide';

export interface BulkSlideTagRequest extends CasesParams {
  operation: BulkSlideTagOperation;
  tagIds: string[];
  resultScopeMode: BulkSlideTagResultScopeMode;
  orchestrationId?: string;
}

export interface BulkSlideTagPreviewSlide {
  slideId: string;
  labId?: string | null;
  caseId?: number | null;
  originalFileName?: string | null;
  stainingType?: string | null;
  expectedSlideTags: BulkSlideTagPreviewTag[];
  ineligibleReason?: string | null;
}

export interface BulkSlideTagPreviewTag {
  tagId: string;
  tagValue?: string | null;
  scope: 'global' | 'study' | 'experiment_result';
  studyId?: string | null;
  orchestrationId?: string | null;
}

export interface BulkSlideTagSelectionAssignment {
  tagId: string;
  studyId?: string | null;
  orchestrationId?: string | null;
}

export interface BulkSlideTagSelectionSlide {
  slideId: string;
  labId?: string | null;
  caseId?: number | null;
  originalFileName?: string | null;
  stainingType?: string | null;
  negativeControl?: boolean | null;
  positiveControl?: boolean | null;
  slideTagAssignments: BulkSlideTagSelectionAssignment[];
}

export interface BulkSlideTagCountByScope {
  globalCount: number;
  studyCount: number;
  experimentResultCount: number;
}

export interface BulkSlideTagCountByTag {
  tagId: string;
  added: number;
  removed: number;
  retained: number;
}

export interface BulkSlideTagSummary {
  targetSlideCount: number;
  eligibleSlideCount: number;
  ineligibleSlideCount: number;
  changedSlideCount: number;
  unchangedSlideCount: number;
  addedAssignmentCount: number;
  removedAssignmentCount: number;
  retainedAssignmentCount: number;
  removedAssignmentsByScope: BulkSlideTagCountByScope;
  countsByTag: BulkSlideTagCountByTag[];
  previewSlides: BulkSlideTagPreviewSlide[];
  previewLimit: number;
}

export interface BulkSlideTagApplyResponse extends BulkSlideTagSummary {
  applied: boolean;
}

export interface BulkSlideTagSelectionResponse {
  slides: BulkSlideTagSelectionSlide[];
  previewSlides: BulkSlideTagSelectionSlide[];
  previewLimit: number;
}

export const getSlideTagOptions = () => {
  return apiRequestHandlerPromise<Array<SlideTag>>({
    url: 'slide_tags',
    method: 'GET',
  });
};

export const getSlideTagDetails = (id: string) => {
  return apiRequestHandlerPromise<SlideTagStudyDetails[]>({
    url: `slide_tags/${id}`,
    method: 'GET',
  });
};

export const createSlideTagOption = ({ tagValue, perStudy, perExperimentResult, uiOrder }: Omit<SlideTag, 'id'>) => {
  return apiRequestHandlerPromise<SlideTag>({
    url: 'slide_tags',
    method: 'POST',
    data: JSON.stringify({ tagValue, perStudy, perExperimentResult, uiOrder }),
  });
};

export const updateSlideTagOption = ({ id, tagValue, perStudy, perExperimentResult, uiOrder }: SlideTag) => {
  return apiRequestHandlerPromise({
    url: `slide_tags/${id}`,
    method: 'POST',
    data: JSON.stringify({ tagValue, perStudy, perExperimentResult, uiOrder }),
  });
};

export const deleteSlideTagOption = (id: string) => {
  return apiRequestHandlerPromise({
    url: `slide_tags/${id}`,
    method: 'DELETE',
  });
};

export const getBulkSlideTagSummary = (data: BulkSlideTagRequest, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<BulkSlideTagSummary>({
    url: 'slide_tags/bulk/summary',
    method: 'POST',
    data: JSON.stringify(data),
    signal,
  });
};

export const getBulkSlideTagSelection = (data: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<BulkSlideTagSelectionResponse>({
    url: 'slide_tags/bulk/selection',
    method: 'POST',
    data: JSON.stringify(data),
    signal,
  });
};

export const applyBulkSlideTags = (data: BulkSlideTagRequest) => {
  return apiRequestHandlerPromise<BulkSlideTagApplyResponse>({
    url: 'slide_tags/bulk/apply',
    method: 'POST',
    data: JSON.stringify(data),
  });
};
